import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useGameStore } from '../../store/gameStore';
import { HUD } from './HUD';
import { LayerRenderer } from './LayerRenderer';
import { LayerTransition } from './LayerTransition';
import { VirtualDpad } from './VirtualDpad';
import { playGameStart, playGameOver, playGameWin, playAscend } from '../../utils/sounds';

function ShareButton({ score, deepest, won }: { score: number; deepest: number; won: boolean }) {
  const [copied, setCopied] = useState(false);

  function handleShare() {
    const result = won ? '🏆 VICTORY!' : '💀 Game Over';
    const text = `${result} I scored ${score} in Nested Gameception — reached Layer ${deepest}! 🎮 https://nested-gameception.rsi.digify.no`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleShare}
      className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition-colors font-mono text-sm"
    >
      {copied ? '✓ Copied!' : '📋 Share Score'}
    </button>
  );
}

function StartScreen() {
  const startGame = useGameStore((s) => s.startGame);
  const highScore = useGameStore((s) => s.highScore);
  const deepestReached = useGameStore((s) => s.deepestReached);
  const runCount = useGameStore((s) => s.runCount);
  // Next run's difficulty label (runCount hasn't been incremented yet)
  const nextRun = runCount + 1;
  const difficultyLabel = nextRun <= 2 ? 'Normal' : nextRun <= 4 ? 'Hard' : nextRun <= 6 ? 'Harder' : 'Brutal';

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code === 'Space' || e.code === 'Enter') {
        playGameStart();
        startGame();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [startGame]);

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-20 px-8 text-center">
      <div>
        <h2 className="text-4xl font-bold text-white mb-3">Nested Gameception</h2>
        <p className="text-gray-400 max-w-md">
          Navigate the Outer World, dive into nested games, and discover games within games.
          How deep can you go?
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 text-sm font-mono">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-500 mb-1">High Score</div>
          <div className="text-yellow-400 text-2xl font-bold">{highScore}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-500 mb-1">Deepest Layer</div>
          <div className="text-purple-400 text-2xl font-bold">{deepestReached}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-500 mb-1">Difficulty</div>
          <div className={`text-2xl font-bold ${nextRun <= 2 ? 'text-green-400' : nextRun <= 4 ? 'text-yellow-400' : nextRun <= 6 ? 'text-orange-400' : 'text-red-400'}`}>
            {difficultyLabel}
          </div>
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-400">
        <div className="flex gap-4 justify-center">
          <span>Layer 0: <span className="text-blue-400">Outer World</span> — collect pellets</span>
        </div>
        <div className="flex gap-4 justify-center">
          <span>Layer 1: <span className="text-green-400">Snake</span> — eat gold food to dive</span>
        </div>
        <div className="flex gap-4 justify-center">
          <span>Layer 2: <span className="text-orange-400">Breakout</span> — break the golden brick</span>
        </div>
        <div className="flex gap-4 justify-center">
          <span>Layer 3: <span className="text-purple-400">Reaction</span> — press Space on cue</span>
        </div>
      </div>

      <button
        onClick={() => { playGameStart(); startGame(); }}
        className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-3 rounded-lg text-lg transition-colors"
      >
        Play Now
      </button>
      <p className="text-xs text-gray-600">or press Space / Enter</p>
    </div>
  );
}

function GameOverScreen() {
  const resetGame = useGameStore((s) => s.resetGame);
  const startGame = useGameStore((s) => s.startGame);
  const totalScore = useGameStore((s) => s.totalScore);
  const highScore = useGameStore((s) => s.highScore);
  const deepestReached = useGameStore((s) => s.deepestReached);

  const playedRef = useRef(false);
  useEffect(() => {
    if (!playedRef.current) {
      playedRef.current = true;
      playGameOver();
    }
  }, []);

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-20 px-8 text-center">
      <div>
        <h2 className="text-4xl font-bold text-red-400 mb-2">Game Over</h2>
        <p className="text-gray-400">Better luck next time!</p>
      </div>

      <div className="grid grid-cols-3 gap-4 font-mono text-sm">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-500 mb-1">Score</div>
          <div className="text-white text-xl font-bold">{totalScore}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-500 mb-1">High Score</div>
          <div className="text-yellow-400 text-xl font-bold">{highScore}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-500 mb-1">Deepest</div>
          <div className="text-purple-400 text-xl font-bold">{deepestReached}</div>
        </div>
      </div>

      <div className="flex gap-4 flex-wrap justify-center">
        <button
          onClick={() => { playGameStart(); startGame(); }}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-2.5 rounded-lg transition-colors"
        >
          Play Again
        </button>
        <ShareButton score={totalScore} deepest={deepestReached} won={false} />
        <button
          onClick={resetGame}
          className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition-colors"
        >
          Main Menu
        </button>
      </div>
    </div>
  );
}

function WinScreen() {
  const resetGame = useGameStore((s) => s.resetGame);
  const startGame = useGameStore((s) => s.startGame);
  const totalScore = useGameStore((s) => s.totalScore);
  const highScore = useGameStore((s) => s.highScore);
  const deepestReached = useGameStore((s) => s.deepestReached);

  const playedRef = useRef(false);
  useEffect(() => {
    if (!playedRef.current) {
      playedRef.current = true;
      playGameWin();
    }
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code === 'Space' || e.code === 'Enter') {
        playGameStart();
        startGame();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [startGame]);

  const isNewRecord = totalScore >= highScore && totalScore > 0;

  const PARTICLES = [
    { left: 10, top: 30, dur: 1.4, delay: 0.0, size: 18, char: '★' },
    { left: 20, top: 60, dur: 1.8, delay: 0.3, size: 14, char: '✦' },
    { left: 35, top: 20, dur: 1.2, delay: 0.6, size: 20, char: '★' },
    { left: 50, top: 50, dur: 1.6, delay: 0.1, size: 16, char: '✦' },
    { left: 65, top: 25, dur: 1.3, delay: 0.4, size: 22, char: '★' },
    { left: 78, top: 55, dur: 1.9, delay: 0.7, size: 12, char: '✦' },
    { left: 90, top: 35, dur: 1.5, delay: 0.2, size: 18, char: '★' },
    { left: 15, top: 70, dur: 1.7, delay: 0.5, size: 14, char: '✦' },
    { left: 45, top: 75, dur: 1.1, delay: 0.8, size: 16, char: '★' },
    { left: 70, top: 70, dur: 2.0, delay: 0.3, size: 12, char: '✦' },
    { left: 85, top: 15, dur: 1.4, delay: 0.9, size: 20, char: '★' },
    { left: 5,  top: 50, dur: 1.6, delay: 0.1, size: 14, char: '✦' },
  ];

  return (
    <div className="flex flex-col items-center justify-center gap-8 py-20 px-8 text-center">
      <div className="relative">
        {/* Sparkle particles */}
        <div className="absolute inset-0 pointer-events-none overflow-visible" aria-hidden="true">
          {PARTICLES.map((p, i) => (
            <span
              key={i}
              className="sparkle absolute text-yellow-400 select-none"
              style={{
                left: `${p.left}%`,
                top: `${p.top}%`,
                fontSize: p.size,
                '--dur': `${p.dur}s`,
                '--delay': `${p.delay}s`,
              } as CSSProperties}
            >
              {p.char}
            </span>
          ))}
        </div>
        <div className="text-5xl font-bold text-yellow-400 mb-2 animate-bounce">VICTORY!</div>
        <p className="text-purple-300 font-mono text-sm">
          You mastered all 4 layers of Gameception
        </p>
        {isNewRecord && (
          <div className="mt-3 inline-block bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full animate-pulse">
            NEW HIGH SCORE!
          </div>
        )}
      </div>

      <div className="text-gray-400 font-mono text-sm bg-gray-800 rounded-lg px-4 py-2">
        +500 win bonus included
      </div>

      <div className="grid grid-cols-3 gap-4 font-mono text-sm">
        <div className="bg-gray-800 rounded-lg p-4 border border-yellow-800">
          <div className="text-gray-500 mb-1">Final Score</div>
          <div className="text-yellow-400 text-xl font-bold">{totalScore}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-yellow-800">
          <div className="text-gray-500 mb-1">High Score</div>
          <div className="text-yellow-400 text-xl font-bold">{highScore}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-purple-800">
          <div className="text-gray-500 mb-1">Deepest</div>
          <div className="text-purple-400 text-xl font-bold">{deepestReached}</div>
        </div>
      </div>

      <div className="flex gap-4 flex-wrap justify-center">
        <button
          onClick={() => { playGameStart(); startGame(); }}
          className="bg-yellow-600 hover:bg-yellow-500 text-black font-bold px-6 py-2.5 rounded-lg transition-colors"
        >
          Play Again
        </button>
        <ShareButton score={totalScore} deepest={deepestReached} won={true} />
        <button
          onClick={resetGame}
          className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition-colors"
        >
          Main Menu
        </button>
      </div>

      <p className="text-xs text-gray-600">or press Space / Enter to play again</p>
    </div>
  );
}

export function GameShell() {
  const phase = useGameStore((s) => s.phase);
  const currentDepth = useGameStore((s) => s.currentDepth);
  const ascend = useGameStore((s) => s.ascend);

  const prevDepthRef = useRef(currentDepth);
  const [goingDeeper, setGoingDeeper] = useState(true);

  useEffect(() => {
    if (phase === 'transitioning') {
      setGoingDeeper(currentDepth > prevDepthRef.current);
    }
    prevDepthRef.current = currentDepth;
  }, [phase, currentDepth]);

  // Global Escape key to ascend
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code === 'Escape' && phase === 'playing' && currentDepth > 0) {
        playAscend();
        ascend();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, currentDepth, ascend]);

  return (
    <div className="bg-gray-950 min-h-[500px]">
      {phase === 'idle' && <StartScreen />}
      {phase === 'game-over' && <GameOverScreen />}
      {phase === 'won' && <WinScreen />}
      {(phase === 'playing' || phase === 'transitioning') && (
        <div className="relative">
          <HUD />
          <div className="relative overflow-hidden">
            <LayerRenderer />
            <LayerTransition visible={phase === 'transitioning'} goingDeeper={goingDeeper} />
          </div>
          <VirtualDpad />
        </div>
      )}
    </div>
  );
}
